import { useAuth } from "../contexts/AuthContext";

/**
 * Hook para verificar permissões do usuário logado
 *
 * @example
 * const { hasPermission, hasAnyPermission, hasAllPermissions, permissoes } = usePermissions();
 *
 * // Verificar uma permissão específica
 * if (hasPermission('criar_militar')) { ... }
 *
 * // Verificar se tem pelo menos uma das permissões
 * if (hasAnyPermission(['criar_militar', 'editar_militar'])) { ... }
 *
 * // Verificar se tem todas as permissões
 * if (hasAllPermissions(['criar_militar', 'editar_militar'])) { ... }
 */
export function usePermissions() {
  const { permissoes, hasPermission, hasAnyPermission, hasAllPermissions } = useAuth();

  return {
    permissoes,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
}

/**
 * Lista de permissões do sistema organizadas por categoria
 */
export const PERMISSOES = {
  // Militares
  CRIAR_ALTERAR_MILITAR: "criar_alterar_militar",
  ALTERAR_SIGLA_MILITAR: "alterar_sigla_militar",
  ALTERAR_NICK: "alterar_nick",
  LISTAR_MILITARES: "listar_militares",
  EDITAR_MILITAR: "editar_militar",

  // Siglas
  CRIAR_SIGLA: "criar_sigla",
  LISTAR_SIGLAS: "listar_siglas",

  // Patentes
  CRIAR_PATENTE: "criar_patente",
  EDITAR_PATENTE: "editar_patente",
  EXCLUIR_PATENTE: "excluir_patente",
  LISTAR_PATENTES: "listar_patentes",

  // Perfis
  CRIAR_PERFIL: "criar_perfil",
  EDITAR_PERFIL: "editar_perfil",
  EXCLUIR_PERFIL: "excluir_perfil",
  LISTAR_PERFIS: "listar_perfis",
  ATRIBUIR_PERFIS: "atribuir_perfis",

  // Permissões
  GERENCIAR_PERMISSOES: "gerenciar_permissoes",
  LISTAR_PERMISSOES: "listar_permissoes",

  // Grupos
  CRIAR_GRUPO: "criar_grupo",
  EDITAR_GRUPO: "editar_grupo",
  EXCLUIR_GRUPO: "excluir_grupo",
  LISTAR_GRUPOS: "listar_grupos",
  GERENCIAR_MEMBROS_GRUPO: "gerenciar_membros_grupo",
  ALTERAR_STATUS_MEMBRO: "alterar_status_membro",

  // Tipos de Grupos
  CRIAR_TIPO_GRUPO: "criar_tipo_grupo",
  EDITAR_TIPO_GRUPO: "editar_tipo_grupo",
  EXCLUIR_TIPO_GRUPO: "excluir_tipo_grupo",
  LISTAR_TIPOS_GRUPO: "listar_tipos_grupo",

  // Cargos de Grupos
  CRIAR_CARGO_GRUPO: "criar_cargo_grupo",
  EDITAR_CARGO_GRUPO: "editar_cargo_grupo",
  EXCLUIR_CARGO_GRUPO: "excluir_cargo_grupo",
  LISTAR_CARGOS_GRUPO: "listar_cargos_grupo",

  // Tipos de Ações
  CRIAR_TIPO_ACAO: "criar_tipo_acao",
  EDITAR_TIPO_ACAO: "editar_tipo_acao",
  EXCLUIR_TIPO_ACAO: "excluir_tipo_acao",
  LISTAR_TIPOS_ACOES: "listar_tipos_acoes",
  GERENCIAR_CAMPOS_TIPO_ACAO: "gerenciar_campos_tipo_acao",

  // Ações de Grupos
  CRIAR_ACAO_GRUPO: "criar_acao_grupo",
  APROVAR_ACAO_GRUPO: "aprovar_acao_grupo",
  REPROVAR_ACAO_GRUPO: "reprovar_acao_grupo",
  LISTAR_ACOES_GRUPO: "listar_acoes_grupo",

  // Guias
  CRIAR_GUIA: "criar_guia",
  EDITAR_GUIA: "editar_guia",
  EXCLUIR_GUIA: "excluir_guia",
  VISUALIZAR_GUIA: "visualizar_guia",
  LISTAR_GUIAS: "listar_guias",

  // Fechamentos
  CRIAR_FECHAMENTO: "criar_fechamento",
  FINALIZAR_FECHAMENTO: "finalizar_fechamento",
  VISUALIZAR_FECHAMENTO: "visualizar_fechamento",
  LISTAR_FECHAMENTOS: "listar_fechamentos",

  // Turmas
  CRIAR_TURMA: "criar_turma",
  EDITAR_TURMA: "editar_turma",
  EXCLUIR_TURMA: "excluir_turma",
  LISTAR_TURMAS: "listar_turmas",
  GERENCIAR_MEMBROS_TURMA: "gerenciar_membros_turma",

  // Notas
  LANCAR_NOTAS: "lancar_notas",
  EDITAR_NOTAS: "editar_notas",
  VISUALIZAR_NOTAS: "visualizar_notas",

  // Provas
  CRIAR_PROVA: "criar_prova",
  EDITAR_PROVA: "editar_prova",
  EXCLUIR_PROVA: "excluir_prova",
  LISTAR_PROVAS: "listar_provas",
  GERENCIAR_QUESTOES: "gerenciar_questoes",
  GERENCIAR_ACESSOS_PROVA: "gerenciar_acessos_prova",
  REALIZAR_PROVA: "realizar_prova",
  CORRIGIR_PROVA: "corrigir_prova",
  DIVULGAR_RESULTADOS: "divulgar_resultados",
  VISUALIZAR_RESULTADOS: "visualizar_resultados",

  // Relatórios
  CRIAR_RELATORIO: "criar_relatorio",
  EDITAR_RELATORIO: "editar_relatorio",
  EXCLUIR_RELATORIO: "excluir_relatorio",
  ENVIAR_RELATORIO: "enviar_relatorio",
  APROVAR_RELATORIO: "aprovar_relatorio",
  REPROVAR_RELATORIO: "reprovar_relatorio",
  VISUALIZAR_RELATORIOS: "visualizar_relatorios",
  LISTAR_RELATORIOS: "listar_relatorios",

  // Status
  CRIAR_STATUS: "criar_status",
  EDITAR_STATUS: "editar_status",
  EXCLUIR_STATUS: "excluir_status",
  LISTAR_STATUS: "listar_status",

  // Honrarias
  CRIAR_HONRARIA: "criar_honraria",
  EDITAR_HONRARIA: "editar_honraria",
  EXCLUIR_HONRARIA: "excluir_honraria",
  LISTAR_HONRARIAS: "listar_honrarias",
  ATRIBUIR_HONRARIA: "atribuir_honraria",
  REMOVER_HONRARIA: "remover_honraria",
  GERENCIAR_HONRARIAS: "gerenciar_honrarias",

  // Medalhas
  CRIAR_MEDALHA: "criar_medalha",
  EDITAR_MEDALHA: "editar_medalha",
  EXCLUIR_MEDALHA: "excluir_medalha",
  LISTAR_MEDALHAS: "listar_medalhas",
  CRIAR_MEDALHA_TIPO: "criar_medalha_tipo",
  EDITAR_MEDALHA_TIPO: "editar_medalha_tipo",
  EXCLUIR_MEDALHA_TIPO: "excluir_medalha_tipo",
  LISTAR_MEDALHAS_TIPOS: "listar_medalhas_tipos",
  ATRIBUIR_MEDALHA: "atribuir_medalha",
  REMOVER_MEDALHA: "remover_medalha",
  GERENCIAR_MEDALHAS: "gerenciar_medalhas",

  // Emblemas
  CRIAR_EMBLEMA: "criar_emblema",
  EDITAR_EMBLEMA: "editar_emblema",
  EXCLUIR_EMBLEMA: "excluir_emblema",
  LISTAR_EMBLEMAS: "listar_emblemas",
  ATRIBUIR_EMBLEMA: "atribuir_emblema",
  REMOVER_EMBLEMA: "remover_emblema",
  GERENCIAR_EMBLEMAS: "gerenciar_emblemas",

  // Patrulhas
  CRIAR_PATRULHA: "criar_patrulha",
  EDITAR_PATRULHA: "editar_patrulha",
  EXCLUIR_PATRULHA: "excluir_patrulha",
  LISTAR_PATRULHAS: "listar_patrulhas",

  // Sistema
  GERENCIAR_CONFIGURACOES: "gerenciar_configuracoes",
  VISUALIZAR_DASHBOARD: "visualizar_dashboard",
  VISUALIZAR_PAINEIS: "visualizar_paineis",
  VISUALIZAR_DESEMPENHO: "visualizar_desempenho",

  // Central
  CENTRAL_RM_GERENCIAR: "central.rm.gerenciar",
  CENTRAL_RMO_GERENCIAR: "central.rmo.gerenciar",
  CENTRAL_FILAS_GERENCIAR: "central.filas.gerenciar",

  // Fórum
  FORUM_VISUALIZAR: "forum.visualizar",
  FORUM_CRIAR_POST: "forum.criar_post",
  FORUM_EDITAR_PROPRIO_POST: "forum.editar_proprio_post",
  FORUM_COMENTAR: "forum.comentar",
  FORUM_EDITAR_PROPRIO_COMENTARIO: "forum.editar_proprio_comentario",
  FORUM_CURTIR: "forum.curtir",
  FORUM_MODERAR: "forum.moderar",
  FORUM_EXCLUIR_PROPRIO: "forum.excluir_proprio",
  FORUM_EXCLUIR_QUALQUER: "forum.excluir_qualquer",
  FORUM_GERENCIAR: "forum.gerenciar",

  // Painéis (legado)
  CRIAR_ALTERAR_PAINEL_OFICIAL: "criar_alterar_painel_oficial",
  CRIAR_ALTERAR_PAINEL_PRACA: "criar_alterar_painel_praca",
  CRIAR_ALTERAR_TREINAMENTO: "criar_alterar_treinamento",

  // Apostilas
  GERENCIAR_APOSTILAS: "gerenciar_apostilas",

  // Destaques
  GERENCIAR_DESTAQUES: "gerenciar_destaques",
  VER_NAO_VOTARAM_DESTAQUE: "ver_nao_votaram_destaque",

  // Site
  GERENCIAR_SITE_CARROSSEL: "gerenciar_site_carrossel",

  // Justificativas de Ausência
  GERENCIAR_JA_TODOS: "gerenciar_ja_todos",
  GERENCIAR_JA_PRACAS: "gerenciar_ja_pracas",

  // Audit Logs
  VISUALIZAR_AUDIT_LOGS: "visualizar_audit_logs",

  // Discord
  GERENCIAR_INTEGRACOES_DISCORD: "gerenciar_integracoes_discord",

  // Habbo Wired
  GERENCIAR_WIRED_CONFIGURACOES: "gerenciar_wired_configuracoes",
  VISUALIZAR_WIRED_LOGS: "visualizar_wired_logs",
  GERENCIAR_WIRED_HUD: "gerenciar_wired_hud",
  VISUALIZAR_WIRED_HUD_OFICIAIS: "visualizar_wired_hud_oficiais",

  // Aniversariantes
  ANIVERSARIANTES_VISUALIZAR: "aniversariantes.visualizar",
  ANIVERSARIANTES_VER_MENSAGENS: "aniversariantes.ver_mensagens",

  // TiTiTinder
  TITITINDER_VISUALIZAR: "tititinder.visualizar",
  TITITINDER_GERENCIAR: "tititinder.gerenciar",

  // TiTiTi Pérolas
  PEROLAS_ENVIAR: "perolas.enviar",
  PEROLAS_GERENCIAR: "perolas.gerenciar",

  // Horários de Oficiais
  VISUALIZAR_HORARIOS_OFICIAIS: "visualizar_horarios_oficiais",
  EDITAR_HORARIOS_PROPRIOS: "editar_horarios_proprios",
  VISUALIZAR_RELATORIOS_HORARIOS_OFICIAIS: "visualizar_relatorios_horarios_oficiais",
  GERENCIAR_CONFIGURACOES_HORARIOS_OFICIAIS: "gerenciar_configuracoes_horarios_oficiais",
} as const;

export type PermissaoKey = keyof typeof PERMISSOES;
export type PermissaoValue = typeof PERMISSOES[PermissaoKey];
